import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { updateFormControlValueAction } from '../actions/formActions'
import { IControlProps, IFormMeta } from '../types/IFormSettings'
import { controlSelector, formKeySelector } from '../selectors/formBuilder.selector'
import { formatCurrency } from '../helpers/currencyFormatter.helper'

// Resolves ambiguity between the thousands- and decimal-separator conventions (e.g. Italian "2.500,50" vs a customer habitually typing the English "2500.50") instead of assuming the control's own locale is what the customer actually typed: a separator followed by 1-2 digits is always the decimal mark (thousands groups are always exactly 3 digits), everything else is stripped as a thousands separator.
function parseLocaleAwareAmount(raw: string): number | null {
	const cleaned = raw.replace(/[^0-9.,]/g, '')
	if (!cleaned) return null

	const lastSeparatorIndex = Math.max(
		cleaned.lastIndexOf(','),
		cleaned.lastIndexOf('.'),
	)
	if (lastSeparatorIndex === -1) {
		const n = Number(cleaned)
		return Number.isNaN(n) ? null : n
	}

	const fractionDigitCount = cleaned.length - lastSeparatorIndex - 1
	const isDecimalMark = fractionDigitCount > 0 && fractionDigitCount <= 2

	const integerPart = cleaned
		.slice(0, lastSeparatorIndex)
		.replace(/[.,]/g, '')
	const fractionPart = cleaned
		.slice(lastSeparatorIndex + 1)
		.replace(/[.,]/g, '')

	const normalized = isDecimalMark
		? `${integerPart}.${fractionPart}`
		: `${integerPart}${fractionPart}`
	const n = Number(normalized)
	return Number.isNaN(n) ? null : n
}

function isEmptyValue(value: unknown): value is '' | undefined | null {
	return value === '' || value === undefined || value === null
}

function AmountInput({ controlName, formName }: IControlProps) {
	const [isFocused, setIsFocused] = useState(false)
	const [inputValue, setInputValue] = useState('')
	const { value, readonly, disabled, placeholder } = useSelector(
		controlSelector(formName, controlName),
	)
	const formMeta = useSelector(
		formKeySelector(formName, 'formMeta'),
	) as IFormMeta

	const dispatch = useDispatch()

	const displayValue = isEmptyValue(value)
		? ''
		: formatCurrency(value, {
				locale: formMeta?.locale,
				code: 'EUR',
				symbol: '',
			}).trim()

	const onFocus = () => {
		setIsFocused(true)
		setInputValue(isEmptyValue(value) ? '' : String(value))
	}

	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.currentTarget.value)
	}

	const onBlurHandler = () => {
		setIsFocused(false)
		const parsed = inputValue === '' ? null : parseLocaleAwareAmount(inputValue)

		dispatch(
			updateFormControlValueAction({
				formName,
				controlName,
				value: parsed ?? '',
			}),
		)
	}

	return (
		<input
			id={controlName}
			name={controlName}
			className="form-control"
			type="text"
			inputMode="decimal"
			value={isFocused ? inputValue : displayValue}
			onFocus={onFocus}
			onChange={onChangeHandler}
			onBlur={onBlurHandler}
			placeholder={placeholder}
			disabled={disabled}
			readOnly={readonly}
		/>
	)
}

export default AmountInput
